import { Conversation, Message as HITLMessage } from '@botpress/hitl-client'
import {
  Composer,
  enrichMessage,
  FileType,
  isFileType,
  RichBlockMessage,
  StylesheetProvider,
  IntegrationMessage as WebchatMessage,
  integrationMessageToBlockMessage as webchatMessageToBlockMessage,
} from '@botpress/webchat'
import { initialConfig, LatestWebchatTheme } from '@bpinternal/shared'
import { Alert, AlertDescription, AlertTitle, ScrollArea } from '@bpinternal/ui-kit-next'
import { useSuspenseQuery } from '@tanstack/react-query'
import { Info } from 'lucide-react'
import { DateTime } from 'luxon'
import { nanoid } from 'nanoid'
import { useEffect, useRef, useState } from 'react'
import { useAsync } from 'react-use'
import { MessageItem } from '~/components/Messages'
import { Spinner } from '~/elements'
import { getUseConfigStore } from '~/features/webchat/stores/useConfigStore'
import { useUploadFile } from '~/hooks'
import { trackEvent } from '~/providers'
import { listConversationUsersQueryOptions } from '~/queries'
import { useLocalStore } from '~/stores'
import { cn } from '~/utils'
import { useSuspenseHITLClient } from '../hooks'
import { historicMessageToBlockMessage } from '../utils'

type HITLChatProps = {
  botId: string
  workspaceId: string
  assignedToCurrentUser?: boolean
  conversation: Conversation
}

export const HITLChat: React.FC<HITLChatProps> = ({
  botId,
  workspaceId,
  assignedToCurrentUser,
  conversation,
}: HITLChatProps) => {
  const getPreferredTheme = useLocalStore((s) => s.getPreferredTheme)
  const webchatTheme: LatestWebchatTheme = {
    ...initialConfig,
    radius: 2,
    themeMode: getPreferredTheme(),
  }
  const useConfigStore = getUseConfigStore({ botId, workspaceId })
  const { botName, botAvatar } = useConfigStore.getState()

  const {
    data: { users },
  } = useSuspenseQuery(listConversationUsersQueryOptions({ conversationId: conversation?.id, botId, workspaceId }))

  const { mutateAsync: uploadFile } = useUploadFile({ botId, workspaceId })

  const hitlClient = useSuspenseHITLClient({ botId, workspaceId })

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [messages, setMessages] = useState<RichBlockMessage[]>([])

  const scrollableMessagesRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const scrollableElement = scrollableMessagesRef.current

    if (scrollableElement) {
      scrollableElement.scrollTop = scrollableElement.scrollHeight
    }
  }, [messages])

  useAsync(async () => {
    setStatus('loading')
    setMessages([])
    let enrichedHistoryMessages: RichBlockMessage[] = []
    let enrichedMessages: RichBlockMessage[] = []
    let status: 'loading' | 'success' | 'error' = 'loading'

    try {
      const { hitlMessageHistory } = await hitlClient.getConversationHistory({ conversationId: conversation.id })
      const blockMessages = hitlMessageHistory.map((message) =>
        historicMessageToBlockMessage(message, conversation.id, conversation.assignedAt)
      )

      enrichedHistoryMessages = enrichMessage(
        blockMessages,
        users,
        conversation.assignee?.id ?? 'Bot',
        botName,
        botAvatar
      )
    } catch (error) {
      console.error('error loading historic messages:', error)
    }

    try {
      const { messages } = await hitlClient.listConversationMessages({ conversationId: conversation.id })
      const blockMessages = messages.map((message) => webchatMessageToBlockMessage(message))
      enrichedMessages = enrichMessage(
        blockMessages,
        users,
        conversation.assignee?.id ?? 'Bot',
        botName,
        botAvatar
      ).reverse()

      status = 'success'
    } catch (error) {
      status = 'error'
      console.error('error loading messages:', error)
    }

    setMessages([...enrichedHistoryMessages, ...enrichedMessages])
    setStatus(status)
  }, [workspaceId, botId, conversation.id])

  useEffect(() => {
    const on = hitlClient.listenConversation({ conversationId: conversation.id })

    const unsubscribeFunctions = [
      on('message_created', (hitlIntegrationMessage) => {
        const webchatIntegrationMessage: WebchatMessage = {
          ...hitlIntegrationMessage,
        }

        const blockMessage = webchatMessageToBlockMessage(webchatIntegrationMessage)
        const enrichedMessage = enrichMessage(
          [blockMessage],
          users,
          conversation.assignee?.id ?? 'Bot',
          botName,
          botAvatar
        )[0]

        if (enrichedMessage) {
          setMessages((prev) => {
            const clientMessageId = enrichedMessage.metadata?.clientMessageId

            if (clientMessageId) {
              const existingMessageIndex = prev.findIndex((m) => m.id === clientMessageId)
              const existingMessage = prev[existingMessageIndex]

              if (existingMessageIndex !== -1) {
                return [
                  ...prev.slice(0, existingMessageIndex),
                  { ...existingMessage, ...enrichedMessage },
                  ...prev.slice(existingMessageIndex + 1),
                ]
              }
            }

            return [...prev, enrichedMessage]
          })
        }
      }),
    ]

    return () => {
      unsubscribeFunctions.forEach((unsubscribe) => unsubscribe())
    }
  })

  const handleSendMessage = async (payload: HITLMessage['payload']) => {
    trackEvent({
      type: 'hitl_conversation_message_sent',
      agentId: conversation.assignee?.workspaceMemberId,
      patientId: conversation.requester?.id,
      conversationId: conversation.id,
      messageType: payload.type,
    })

    const temporaryId = nanoid()

    const temporaryMessage: WebchatMessage = {
      id: temporaryId,
      createdAt: new Date().toISOString(),
      userId: conversation.assignee!.id,
      conversationId: conversation.id,
      payload,
      metadata: {
        clientMessageId: temporaryId,
      },
    }

    setMessages((prev) => {
      const blockMessage = webchatMessageToBlockMessage(temporaryMessage)
      const enrichedMessage = enrichMessage(
        [blockMessage],
        users,
        conversation.assignee?.id ?? 'Bot',
        botName,
        botAvatar
      )[0]

      if (enrichedMessage) return [...prev, enrichedMessage]

      return prev
    })

    await hitlClient.createMessage({
      payload,
      conversationId: conversation.id,
      metadata: temporaryMessage.metadata,
    })
  }

  const handleFileUpload = async (file: File): Promise<{ fileUrl: string; name: string; type: FileType }> => {
    const thirtyDaysFromNow = new Date()
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30)

    const uploadResponse = await uploadFile({
      key: `${file.name}-${conversation.id}`,
      content: file,
      contentType: file.type,
      expiresAt: thirtyDaysFromNow.toISOString(),
      accessPolicies: ['public_content'],
      tags: { source: 'integration', integration: 'hitl' },
    })

    if (!uploadResponse) {
      throw new Error('Failed to upload file')
    }

    const contentType = uploadResponse.file.contentType.split('/').shift() ?? ''

    return {
      fileUrl: uploadResponse.file.url,
      name: file.name,
      type: isFileType(contentType) ? contentType : ('file' as const),
    }
  }

  const shouldShowEscalatedMarker = (
    conversation: Conversation,
    currentMessage: RichBlockMessage,
    nextMessage: RichBlockMessage | undefined
  ): boolean => {
    if (!conversation.assignedAt) return false

    const assignedAtTime = parseInt(conversation.assignedAt)
    const currentMessageTime = currentMessage?.timestamp?.getTime() ?? 0

    // Show marker if current message is before assignment
    if (assignedAtTime <= currentMessageTime) return false

    // Show marker if next message is after assignment (or if there's no next message)
    const nextMessageTime = nextMessage?.timestamp?.getTime() ?? Infinity
    return assignedAtTime < nextMessageTime
  }

  return (
    <>
      <StylesheetProvider {...webchatTheme} />
      <ScrollArea
        className="relative h-[calc(100vh-15rem)] max-w-full border-x border-neutral-400 px-8"
        ref={scrollableMessagesRef}
      >
        {status === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Spinner size="5" />
          </div>
        )}
        {status === 'error' && (
          <div className="absolute inset-0 mx-10 flex items-center justify-center">
            <Alert>
              <Info className="h-5 w-5" />
              <AlertTitle>Failed to load conversation messages</AlertTitle>
              <AlertDescription>Please try again later.</AlertDescription>
            </Alert>
          </div>
        )}
        {status === 'success' && (
          <>
            <div className="mb-16 flex h-full flex-col gap-2">
              {messages.length > 0 ? (
                messages.map((message, i, arr) => (
                  <div
                    key={message.metadata?.clientMessageId || message.id}
                    className={cn({ ['mb-24']: i === arr.length - 1 })}
                  >
                    <MessageItem disableLink {...message} />
                    {shouldShowEscalatedMarker(conversation, message, messages[i + 1]) ? (
                      <EscalatedMarker date={new Date(parseInt(conversation.assignedAt))} />
                    ) : null}
                  </div>
                ))
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-muted-foreground">There are no messages in this conversation</p>
                </div>
              )}
            </div>
            <div className="absolute inset-x-0 bottom-0 bg-background px-4 pb-2 pt-1">
              <div className="absolute inset-x-0 -top-8 h-8 bg-gradient-to-t from-background to-transparent" />
              <Composer
                sendMessage={(payload) => handleSendMessage(payload as HITLMessage['payload'])}
                uploadFile={(file) => handleFileUpload(file)}
                composerPlaceholder={assignedToCurrentUser ? 'Enter message...' : 'You must be assigned to respond'}
                footer={''}
                connected={assignedToCurrentUser && !!hitlClient}
                allowFileUpload={true}
              />
            </div>
          </>
        )}
      </ScrollArea>
    </>
  )
}

const EscalatedMarker = ({ date }: { date: Date }) => (
  <div className="my-3 mb-1 flex w-full items-center justify-center">
    <div className="ml-8 h-[1px] flex-grow bg-neutral-600" />
    <div className="mx-4 text-sm text-neutral-600">
      Conversation Escalated &#8226; {DateTime.fromJSDate(date).toRelative()}
    </div>
    <div className="mr-8 h-[1px] flex-grow bg-neutral-600" />
  </div>
)
